import { useState } from "react";
import { MousePointerClick, Eye, Sparkles, Layers, Search } from "lucide-react";
import { fmtNum, prettyType } from "./pmaxUtils";

export const DeltaPill = ({ delta }) => {
  if (!delta) return null;
  if (delta.direction === "flat") {
    return <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-700/40 border border-slate-600/40 text-slate-400">flat</span>;
  }
  const up = delta.direction === "up";
  const cls = up ? "bg-lime-400/15 border-lime-400/30 text-lime-300" : "bg-amber-400/10 border-amber-400/25 text-amber-300";
  return (
    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums border ${cls}`} data-testid="pmax-delta-pill">
      {up ? "▲ +" : "▼ "}{Math.abs(delta.pct).toFixed(Math.abs(delta.pct) >= 10 ? 0 : 1)}%
    </span>
  );
};

export const Kpi = ({ label, value, sub, delta, testId }) => (
  <div className="bg-[#0c465f]/70 border border-cyan-300/15 rounded-xl p-4" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/70 font-bold mb-1">{label}</p>
    <div className="flex items-baseline gap-2 flex-wrap">
      <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">{value}</p>
      <DeltaPill delta={delta} />
    </div>
    {sub && <p className="text-slate-400 text-xs mt-1">{sub}</p>}
  </div>
);

export const Panel = ({ title, icon: Icon, children, testId }) => (
  <div className="bg-slate-900/40 border border-white/10 rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-4 h-4 text-lime-300" />}
      <p className="text-white font-semibold text-sm">{title}</p>
    </div>
    {children}
  </div>
);

export const WeeklyBars = ({ weekly = [] }) => {
  const max = Math.max(...weekly.map((w) => w.events || 0), 1);
  return (
    <div className="flex items-end gap-2 md:gap-3 h-40" data-testid="pmax-weekly-chart">
      {weekly.map((w, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group">
          <span className="text-white text-xs font-semibold tabular-nums mb-1 opacity-80 group-hover:opacity-100">{fmtNum(w.events)}</span>
          <div
            className="w-full rounded-t-lg bg-gradient-to-t from-cyan-500/70 to-lime-300/90 transition-[height] duration-700"
            style={{ height: `${Math.max(4, ((w.events || 0) / max) * 100)}%` }}
          />
          <span className="text-[10px] text-slate-400 mt-1.5 text-center leading-tight">{(w.week || "").replace(/,\s*\d{4}$/, "")}</span>
        </div>
      ))}
    </div>
  );
};

export const ShareBars = ({ rows = [], valueKey = "pct", suffix = "%", testId }) => {
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  return (
    <div className="space-y-2.5" data-testid={testId}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-200">{prettyType(r.type)}</span>
            <span className="text-white font-semibold tabular-nums">{valueKey === "count" ? fmtNum(r[valueKey]) : Number(r[valueKey] || 0).toFixed(2)}{suffix}</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-lime-300" style={{ width: `${((Number(r[valueKey]) || 0) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const ConvList = ({ title, total, rows, icon: Icon, testId }) => (
  <div className="bg-[#0c465f]/50 border border-lime-300/15 rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-lime-300" />
      <p className="text-[11px] uppercase tracking-[0.14em] text-lime-200/80 font-bold">{title}</p>
    </div>
    <p className="text-white text-3xl font-bold tabular-nums mb-3">{fmtNum(total)}</p>
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between gap-3 text-xs">
          <span className="text-slate-300 truncate" title={r.action}>{r.action}</span>
          <span className="text-white font-semibold tabular-nums">{fmtNum(r.count)}</span>
        </div>
      ))}
    </div>
  </div>
);

export const Conversions = ({ rec }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <ConvList title="After clicking an ad" total={rec.click_conversions_total} rows={rec.click_conversions || []} icon={MousePointerClick} testId="pmax-click-conversions" />
    <ConvList title="After seeing an ad" total={rec.view_through_total} rows={rec.view_through_conversions || []} icon={Eye} testId="pmax-view-conversions" />
  </div>
);

export const Extensions = ({ extensions = [], types = [] }) => {
  const top = [...extensions].sort((a, b) => (b.event_rate || 0) - (a.event_rate || 0)).slice(0, 5);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 space-y-2" data-testid="pmax-top-extensions">
        {top.map((e, i) => (
          <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2.5 border border-white/5">
            <span className="text-lime-300 font-bold text-sm w-5 tabular-nums">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate" title={e.text}>{(e.text || "").replace(/\.,\s*/g, " · ")}</p>
              <p className="text-[10px] uppercase tracking-wider text-cyan-300/70">{prettyType(e.type)} · {fmtNum(e.events)} actions</p>
            </div>
            <span className="text-lime-300 font-bold text-sm tabular-nums">{Number(e.event_rate || 0).toFixed(1)}%</span>
          </div>
        ))}
      </div>
      {types.length > 0 && (
        <div className="lg:col-span-2">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-3">Engagement rate by extension type</p>
          <ShareBars rows={types} valueKey="event_rate" testId="pmax-extension-types" />
        </div>
      )}
    </div>
  );
};

export const ThemesAndAssets = ({ rec }) => {
  const [showAll, setShowAll] = useState(false);
  const themes = rec.search_themes || [];
  const shown = showAll ? themes : themes.slice(0, 14);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel title="Search themes Google matched your ads to" icon={Search} testId="pmax-search-themes">
        <div className="flex flex-wrap gap-1.5">
          {shown.map((t, i) => (
            <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-cyan-400/10 border border-cyan-300/20 text-cyan-100">{t}</span>
          ))}
        </div>
        {themes.length > 14 && (
          <button onClick={() => setShowAll((v) => !v)} className="mt-3 text-xs text-lime-300 hover:text-lime-200 font-semibold" data-testid="pmax-themes-toggle">
            {showAll ? "Show fewer" : `Show all ${themes.length}`}
          </button>
        )}
      </Panel>
      <Panel title="Ad pieces Google AI built" icon={Layers} testId="pmax-creative-assets">
        <div className="flex items-baseline gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-lime-300" />
          <p className="text-white text-4xl font-bold tabular-nums" data-testid="pmax-creative-total">{fmtNum(rec.creative_assets_total)}</p>
          <p className="text-slate-400 text-xs">headlines, images and videos</p>
        </div>
        <ShareBars rows={rec.creative_types || []} valueKey="count" suffix="" testId="pmax-creative-types" />
      </Panel>
    </div>
  );
};
